import { useState } from "react";
import { toast } from "sonner";
import { Loader2, BadgeCheck, Timer, Euro } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import RgpdCheckbox from "@/components/RgpdCheckbox";

const inputCls =
  "w-full h-12 rounded-xl bg-[#141414] border border-white/10 px-4 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF1E27] outline-none";

const STEPS = [
  { icon: BadgeCheck, title: "Vous décrivez votre véhicule", text: "Marque, modèle, année, kilométrage et état général : 2 minutes suffisent." },
  { icon: Timer, title: "Estimation sous 24 h", text: "Nous étudions votre demande et revenons vers vous avec une offre claire et argumentée." },
  { icon: Euro, title: "Rachat cash ou dépôt-vente", text: "Paiement immédiat ou mise en vente au showroom avec notre réseau : vous choisissez." },
];

export default function TradeIn() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "", brand: "", model: "",
    year: "", km: "", desired_price: "", description: "",
  });
  const [rgpd, setRgpd] = useState(false);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!rgpd) return toast.error("Merci d'accepter le traitement de vos données (RGPD).");
    setSending(true);
    try {
      await api.post("/deposits", {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        brand: form.brand,
        model: form.model,
        year: parseInt(form.year, 10),
        km: parseInt(form.km, 10),
        desired_price: form.desired_price ? parseInt(form.desired_price, 10) : null,
        description: form.description,
        rgpd_consent: rgpd,
      });
      toast.success("Demande envoyée ! Estimation sous 24 h ouvrées.");
      setForm({ name: "", phone: "", email: "", brand: "", model: "", year: "", km: "", desired_price: "", description: "" });
      setRgpd(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="trade-in-page" className="pt-28 pb-24 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-spec text-xs uppercase tracking-[0.3em] text-[#FF1E27] mb-3">Reprise & dépôt-vente</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight max-w-3xl">
            Vendez votre voiture, <span className="text-[#FF1E27]">on s'occupe du reste</span>
          </h1>
        </Reveal>

        <div className="mt-12 grid gap-12 lg:grid-cols-5">
          <div className="lg:col-span-2 space-y-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.12}>
                <div className="flex gap-4" data-testid={`trade-in-step-${i + 1}`}>
                  <div className="w-12 h-12 shrink-0 rounded-full border border-[#FF1E27]/40 bg-[#FF1E27]/10 flex items-center justify-center">
                    <s.icon className="w-5 h-5 text-[#FF1E27]" />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg">{s.title}</h3>
                    <p className="text-sm text-zinc-400 leading-relaxed mt-1">{s.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15} className="lg:col-span-3">
            <form onSubmit={submit} data-testid="trade-in-form" className="bg-[#141414] border border-white/10 rounded-2xl p-6 sm:p-8 space-y-4">
              <h2 className="font-display text-xl font-bold uppercase tracking-tight">Estimation gratuite</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="trade-in-name-input" required value={form.name} onChange={set("name")} placeholder="Nom & prénom *" className={inputCls} />
                <input data-testid="trade-in-phone-input" required value={form.phone} onChange={set("phone")} placeholder="Téléphone *" className={inputCls} />
              </div>
              <input data-testid="trade-in-email-input" type="email" value={form.email} onChange={set("email")} placeholder="Email (optionnel)" className={inputCls} />
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="trade-in-brand-input" required value={form.brand} onChange={set("brand")} placeholder="Marque * (ex. BMW)" className={inputCls} />
                <input data-testid="trade-in-model-input" required value={form.model} onChange={set("model")} placeholder="Modèle * (ex. Série 3 320d)" className={inputCls} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <input data-testid="trade-in-year-input" required type="number" min="1980" max="2026" value={form.year} onChange={set("year")} placeholder="Année *" className={inputCls} />
                <input data-testid="trade-in-km-input" required type="number" min="0" value={form.km} onChange={set("km")} placeholder="Km *" className={inputCls} />
                <input data-testid="trade-in-price-input" type="number" min="0" value={form.desired_price} onChange={set("desired_price")} placeholder="Prix souhaité €" className={inputCls} />
              </div>
              <textarea data-testid="trade-in-description-input" rows={4} value={form.description} onChange={set("description")}
                placeholder="État général, options, entretien, liens de photos…"
                className="w-full rounded-xl bg-[#0d0d0d] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:border-[#FF1E27] outline-none resize-none" />
              <RgpdCheckbox checked={rgpd} onChange={setRgpd} testid="trade-in-rgpd-checkbox" />
              <button data-testid="trade-in-form-submit-btn" type="submit" disabled={sending}
                className="h-12 px-8 rounded-full bg-[#FF1E27] text-white font-semibold flex items-center gap-2 hover:bg-[#E63946] transition-colors disabled:opacity-60">
                {sending && <Loader2 className="w-4 h-4 animate-spin" />} Envoyer ma demande de rachat
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </main>
  );
}
